import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { Folder, FileBox, Search, Plus, X, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const CREATE_CASE_ROLES = ['LawEnforcement', 'Attorney', 'CaseManager', 'SystemAdmin'];

export default function Cases() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [newCaseTitle, setNewCaseTitle] = useState('');
  const [newCaseType, setNewCaseType] = useState<'Criminal' | 'Civil'>('Criminal');
  const [newCaseDescription, setNewCaseDescription] = useState('');
  const [newCaseCourt, setNewCaseCourt] = useState('');

  const { data: cases, isLoading, error } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  const createCaseMutation = useMutation({
    mutationFn: () => apiClient.createCase({
      title: newCaseTitle,
      type: newCaseType,
      description: newCaseDescription || undefined,
      court: newCaseCourt || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      setShowNewCaseModal(false);
      setNewCaseTitle('');
      setNewCaseType('Criminal');
      setNewCaseDescription('');
      setNewCaseCourt('');
    },
  });

  const canCreateCase = user && CREATE_CASE_ROLES.includes(user.role);

  const filteredCases = cases?.filter((caseItem) => {
    if (caseItem.caseNumber === 'PUBLIC-SUBMISSION') return false;
    const matchesSearch =
      caseItem.caseNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || caseItem.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load cases. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Cases</h1>
          <p className="text-gray-500 mt-2">
            Browse and manage evidence cases
          </p>
        </div>
        {canCreateCase && (
          <button
            onClick={() => setShowNewCaseModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-justice-navy text-white rounded-xl text-sm font-medium hover:bg-justice-navy/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Case
          </button>
        )}
      </div>

      {/* New Case Modal */}
      {showNewCaseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Create New Case</h2>
              <button onClick={() => setShowNewCaseModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); createCaseMutation.mutate(); }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={newCaseTitle}
                  onChange={(e) => setNewCaseTitle(e.target.value)}
                  placeholder="e.g. State v. Doe - Theft Investigation"
                  className="w-full px-3 py-2.5 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-justice-navy outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Type <span className="text-red-500">*</span></label>
                <select
                  value={newCaseType}
                  onChange={(e) => setNewCaseType(e.target.value as 'Criminal' | 'Civil')}
                  className="w-full px-3 py-2.5 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-justice-navy outline-none text-sm"
                >
                  <option value="Criminal">Criminal</option>
                  <option value="Civil">Civil</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Court (Optional)</label>
                <input
                  type="text"
                  value={newCaseCourt}
                  onChange={(e) => setNewCaseCourt(e.target.value)}
                  placeholder="e.g. Superior Court of California"
                  className="w-full px-3 py-2.5 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-justice-navy outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={newCaseDescription}
                  onChange={(e) => setNewCaseDescription(e.target.value)}
                  rows={3}
                  placeholder="Brief description of the case..."
                  className="w-full px-3 py-2.5 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-justice-navy outline-none text-sm resize-none"
                />
              </div>
              {createCaseMutation.isError && (
                <p className="text-sm text-red-600">Failed to create case. Please try again.</p>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewCaseModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl ring-1 ring-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newCaseTitle.trim() || createCaseMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-justice-navy text-white rounded-xl text-sm font-medium hover:bg-justice-navy/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createCaseMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</>
                  ) : 'Create Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search cases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border-0 rounded-xl shadow-sm ring-1 ring-gray-200 focus:ring-2 focus:ring-justice-navy placeholder:text-gray-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border-0 rounded-xl shadow-sm ring-1 ring-gray-200 px-4 py-2.5 focus:ring-2 focus:ring-justice-navy text-sm font-medium text-gray-700"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Cases list */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading cases...</p>
        </div>
      ) : filteredCases?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Folder className="h-12 w-12 text-gray-400 mx-auto" />
          <p className="text-gray-500 mt-4">No cases found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Case
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jurisdiction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Evidence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCases?.map((caseItem) => (
                <tr key={caseItem.caseNumber} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link
                      to={`/cases/${caseItem.caseNumber}`}
                      className="flex items-center gap-3"
                    >
                      <Folder className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 hover:text-primary-600">
                          {caseItem.caseNumber}
                        </p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">
                          {caseItem.title}
                        </p>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {caseItem.jurisdiction}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        caseItem.status === 'open'
                          ? 'bg-green-100 text-green-800'
                          : caseItem.status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : caseItem.status === 'closed'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {caseItem.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <FileBox className="h-4 w-4" />
                      {caseItem.evidenceCount ?? 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {format(new Date(caseItem.createdAt), 'MMM d, yyyy')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
